import c from 'classnames';
import { IconChevronDown, IconX } from 'featherico';
import { getOr } from 'lodash/fp';
import moment from 'moment';
import type * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Manager, Popper, Reference } from 'react-popper';
import { useAppContext } from '../../../../hooks/useAppContext';
import { AriaAnnouncer } from '../../../AriaHelper';
import ClickOutside from '../../../ClickOutside';
import { InputButtonIcon } from '../../../Input/InputButton';
import { InputIcon } from '../../../Input/InputIcon';
import getIsDateInRangeError from '../../BasePicker/Validators/isDateInRange';
import Popup from '../Popup';
import styles from './styles.m.less';

export const KINDS_CLASSMAP = {
    DEFAULT: c(styles.kindDefault),
    TITLE: c(styles.kindTitle),
};

export const KINDS = Object.keys(KINDS_CLASSMAP);

interface Props {
    date: moment.Moment | undefined;
    onChange: (date: moment.Moment | undefined) => void;
    formatPrefix?: (date: moment.Moment | undefined) => React.ReactNode | undefined;
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => void;

    isClearable: boolean;
    kind: keyof typeof KINDS_CLASSMAP;
    highlightedDates: string[];
    validRange: { from?: string; to?: string };
    placeholderText: string;
    showIcon: boolean;
    disabled: boolean;
    readOnly: boolean;
    id?: string;
    hasError: boolean;
    isSmall?: boolean;
    dataIntercomTarget?: string;
}

export default function Picker(props: Props) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const popupRef = useRef<HTMLElement | null>(null);
    const isInteractive = !props.disabled && !props.readOnly;

    const [isFocussed, setIsFocussed] = useState(false);
    const [skipBlur, setSkipBlur] = useState(false);

    const [isPickerOpen, setPickerOpen] = useState(false);
    const closePicker = () => setPickerOpen(false);

    const [inputValue, setInputValue, inputError] = useInput(
        props.date,
        props.onChange,
        props.validRange,
        props.isClearable
    );

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            closePicker();
        }
    };

    const handleChange = (date?: moment.Moment) => {
        props.onChange(date);
        closePicker();
    };

    const handleClick = () => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
        setIsFocussed(true);
    };

    const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
        setIsFocussed(false);
        if (skipBlur) {
            setSkipBlur(false);
            return;
        }
        const isWithinPopup = popupRef.current?.contains(event.target);
        if (isWithinPopup) {
            return;
        }
        closePicker();
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setPickerOpen(true);
        setIsFocussed(true);
        props.onFocus(e);
    };

    const handlePopupMouseDown = () => setSkipBlur(true);

    const prefix = props.formatPrefix && props.formatPrefix(props.date);

    const showClearButton =
        props.showIcon && props.isClearable && inputValue !== '' && !props.disabled && !props.readOnly;
    const clearValue = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setInputValue('');
        closePicker();
    };

    return (
        <div className={c(KINDS_CLASSMAP[props.kind], styles.wrapper)} onBlur={handleBlur}>
            <ClickOutside onClickOutside={closePicker} enabled={isPickerOpen}>
                <Manager>
                    <Reference>
                        {({ ref }) => (
                            <>
                                <div
                                    ref={ref}
                                    className={c(styles.box, {
                                        [styles.withIcon]: props.showIcon,
                                        [styles.invalid]: inputError || props.hasError,
                                        [styles.focus]: isFocussed,
                                        [styles.disabled]: props.disabled,
                                        [styles.readOnly]: props.readOnly,
                                        [styles.isSmall]: props.isSmall,
                                    })}
                                    onClick={isInteractive ? handleClick : undefined}
                                    role="textbox"
                                >
                                    {prefix}

                                    <input
                                        type="text"
                                        value={inputValue || ''}
                                        maxLength={10}
                                        className={styles.input}
                                        ref={inputRef}
                                        onChange={e => setInputValue(e.target.value)}
                                        onFocus={isInteractive ? handleFocus : undefined}
                                        onKeyDown={isInteractive ? handleKeyDown : undefined}
                                        placeholder={props.placeholderText}
                                        aria-label={props.placeholderText}
                                        id={props.id}
                                        disabled={props.disabled}
                                        readOnly={props.readOnly}
                                        data-intercom-target={props.dataIntercomTarget}
                                    />

                                    {props.showIcon && (
                                        <InputIcon
                                            small
                                            className={c(styles.chevron, {
                                                [styles.hideOnHover]: showClearButton,
                                                [styles.disabled]: props.disabled,
                                            })}
                                            aria-hidden={true}
                                        >
                                            <IconChevronDown />
                                        </InputIcon>
                                    )}
                                </div>

                                {showClearButton && (
                                    <InputButtonIcon
                                        className={styles.clearButton}
                                        onClick={isInteractive ? clearValue : undefined}
                                        size={props.isSmall ? 'SMALL' : props.kind}
                                        aria-label="Clear Date"
                                    >
                                        <IconX />
                                    </InputButtonIcon>
                                )}
                            </>
                        )}
                    </Reference>
                    {isPickerOpen && (
                        <Popper placement="bottom-start" innerRef={innerRef => (popupRef.current = innerRef)}>
                            {({ ref, style, placement }) => (
                                <div
                                    ref={ref}
                                    style={style}
                                    className={c(
                                        styles.popup,
                                        placement?.includes('bottom') ? styles.popupBottom : styles.popupTop
                                    )}
                                >
                                    <Popup
                                        date={props.date}
                                        onChange={handleChange}
                                        errorMessage={inputError}
                                        onMouseDown={handlePopupMouseDown}
                                        highlightedDates={props.highlightedDates}
                                        validRange={props.validRange}
                                    />
                                </div>
                            )}
                        </Popper>
                    )}
                    {inputError && <AriaAnnouncer ariaRole="alert">{inputError}</AriaAnnouncer>}
                </Manager>
            </ClickOutside>
        </div>
    );
}

function sameDate(a: moment.Moment | undefined, b: moment.Moment | undefined): boolean {
    if (!a && !b) return true;
    if (!!a && !!b) return a.isSame(b, 'day');
    return false;
}

function useInput(
    date: moment.Moment | undefined,
    onChange: (date: moment.Moment | undefined) => void,
    validRange: { from?: string; to?: string },
    isClearable: boolean
) {
    const appContext = useAppContext();
    const dateFormat = getOr(
        moment.HTML5_FMT.DATE,
        'user.company.companySettings.dateFormatCoarse',
        appContext
    ) as string;

    const [inputValue, setInputValue] = useState(date ? date.format(dateFormat) : '');
    const [inputError, setInputError] = useState<string | null>(null);

    useEffect(() => {
        if (!sameDate(date, inputValue ? moment(inputValue, dateFormat, true) : undefined)) {
            setInputValue(date ? date.format(dateFormat) : '');
        }

        setInputError(null);
    }, [date, dateFormat]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleInputValue = useCallback(
        (newInputValue: string) => {
            if (newInputValue === '' && isClearable) {
                setInputValue('');
                setInputError(null);
                onChange(undefined);
                return;
            }

            setInputValue(newInputValue);
            const newDate = moment(newInputValue, dateFormat, true);

            if (!newDate.isValid()) {
                return setInputError('Please check the entered date format');
            }

            const dateInRangeError = getIsDateInRangeError(newDate, validRange, dateFormat);
            if (dateInRangeError) {
                return setInputError(dateInRangeError);
            }

            setInputError(null);
            onChange(newDate);
        },
        [onChange, dateFormat, validRange, isClearable]
    );

    return [inputValue, handleInputValue, inputError] as const;
}
